/**
 * Character creation contracts shared by the title UI and authoritative server.
 *
 * Creation is deliberately versioned. A saved character is player progress, not
 * derived world data: it remembers which generated hometown was selected even if
 * a later worldgen version changes the surrounding census.
 */
import type { CharacterStyle } from "./rpg.js";
import { validateStyle, canonicalWardrobeToken } from "./rpg.js";
import { homeSystemId } from "./universe.js";

export const CHARACTER_RECORD_VERSION = 1 as const;
export const CHARACTER_INTRO_ID = "homecoming-v1" as const;
export const CHARACTER_NAME_MIN = 3;
export const CHARACTER_NAME_MAX = 24;

/** Multi-character accounts (2026-08-03): every account may hold up to this
 *  many characters, each stored at users/{uid}/characters/{cid}. */
export const CHARACTER_SLOTS_MAX = 5;

/** Character ids are server-allocated slot tokens: `c1`, `c2`, … They embed
 *  into home-system ids (`home:<uid>.<cid>`) and cache-instance keys, so the
 *  alphabet is deliberately tiny and never client-chosen. */
const CHARACTER_ID_RE = /^c\d{1,3}$/;

/**
 * Highest slot NUMBER an account can ever mint, bounded by the id grammar above.
 *
 * Deliberately far above {@link CHARACTER_SLOTS_MAX}: a deleted character keeps
 * its slot forever (its doc is archived there), so slot ids only ever climb.
 * The 5-character limit is enforced by counting LIVE characters, not by running
 * out of ids — otherwise a player who deleted and re-rolled five times would be
 * locked out with an empty roster.
 */
export const CHARACTER_SLOT_ID_MAX = 999;

export function isCharacterId(raw: unknown): raw is string {
  return typeof raw === "string" && CHARACTER_ID_RE.test(raw);
}

export type CharacterCreationStatus = "intro" | "active";

/**
 * Story-stage lifecycle (SOUL kickoff — docs/design/soul-kickoff.md).
 *
 * Additive to the v1 record: a record WITHOUT `stage` follows the legacy
 * homecoming intro flow untouched; a record WITH `stage` follows the staged
 * kickoff. Stages advance strictly in order (server-validated); the final
 * `prologue:founding → settled` transition also flips `status`/`intro.completed`
 * so every existing active-character gate keeps working unchanged. New story
 * chapters extend this list — the order IS the arc.
 */
export const STORY_STAGES = [
  "prologue:landfall",
  "prologue:salvage",
  "prologue:homeward",
  "prologue:founding",
  "settled",
] as const;

export type StoryStageId = (typeof STORY_STAGES)[number];

export function isStoryStage(raw: unknown): raw is StoryStageId {
  return typeof raw === "string" && (STORY_STAGES as readonly string[]).includes(raw);
}

export function isPrologueStage(stage: StoryStageId | undefined): boolean {
  return stage !== undefined && stage.startsWith("prologue:");
}

/** Stages advance one step at a time; no skips, no regressions. */
export function canAdvanceStage(from: StoryStageId, to: StoryStageId): boolean {
  const i = STORY_STAGES.indexOf(from);
  return i >= 0 && STORY_STAGES.indexOf(to) === i + 1;
}

export interface CharacterHometownV1 {
  systemId: string;
  planetIndex: 0;
  settlementId: string;
  settlementName: string;
  capitalSettlementId: string;
  capitalName: string;
  /** Great-circle surface distance between hometown and capital. */
  distanceToCapitalM: number;
  /** Stable planet-local ground anchor (metres from the planet centre). */
  localPosition: [number, number, number];
  /** Optional additive v1 field: exact planet-local capital centre for navigation. */
  capitalLocalPosition?: [number, number, number];
  /** Stable planet-local tangent heading, unit length. */
  localFacing: [number, number, number];
}

export interface CharacterRecordV1 {
  v: typeof CHARACTER_RECORD_VERSION;
  /** Server-allocated character id (`c<slot>`) — the subcollection doc id. */
  id: string;
  status: CharacterCreationStatus;
  name: string;
  /** Canonical case-insensitive uniqueness key. Never rendered. */
  nameKey: string;
  style: CharacterStyle;
  hometown: CharacterHometownV1;
  intro: { id: typeof CHARACTER_INTRO_ID; completed: boolean };
  /** Optional additive v1 field: SOUL-kickoff story stage. Absent = legacy homecoming flow. */
  stage?: StoryStageId;
  /**
   * Optional additive v1 field: cosmetic unlock ids earned in play (cache clears grant
   * `"<part>:<variant>"` ids from the existing STYLE_VARIANTS wardrobe). Absent = the
   * character has unlocked nothing, which is every legacy record.
   */
  unlocks?: string[];
  /**
   * Optional additive v1 field: epoch ms this character was deleted.
   *
   * Deletion is a FLAG, never a destruction (user call 2026-08-04). The record,
   * its progression doc, and everything keyed to its `cid` stay exactly where
   * they are — which is what makes deletion safe: nothing else in the world has
   * to be hunted down and reconciled (homestead claims carry the account uid,
   * market payouts carry a `sellerCid`, cache clears carry a slot key), and no
   * late write can resurrect a half-removed character.
   *
   * Absent = a live character; every legacy record therefore reads as live.
   * A flagged record is filtered out of the roster and refused by every gate,
   * so it can never be played, activated, advanced, or granted anything.
   */
  deletedAt?: number;
  createdAt: number;
  updatedAt: number;
}

/** True when a record has been deleted (flagged, not destroyed). */
export function isDeletedCharacter(record: CharacterRecordV1): boolean {
  return record.deletedAt !== undefined;
}

export interface CharacterCreationDraft {
  name: string;
  style: CharacterStyle;
  /** Opt into the staged SOUL-kickoff prologue instead of the legacy homecoming intro. */
  kickoff?: boolean;
}

export type CharacterNameProblem = "required" | "length" | "characters";

export type CharacterNameResult =
  | { ok: true; display: string; key: string }
  | { ok: false; problem: CharacterNameProblem };

/**
 * Human-readable names with an intentionally small canonical alphabet. This
 * avoids Unicode confusables and locale-dependent case folding in the global
 * reservation index while still allowing callsigns such as `Kestrel-9` and
 * `Oren Vale`. We can widen this through a versioned normalizer later.
 */
export function normalizeCharacterName(raw: unknown): CharacterNameResult {
  if (typeof raw !== "string") return { ok: false, problem: "required" };
  const display = raw
    .normalize("NFKC")
    .replace(/[\u2018\u2019]/g, "'")
    .trim()
    .replace(/\s+/g, " ");
  if (!display) return { ok: false, problem: "required" };
  if (display.length < CHARACTER_NAME_MIN || display.length > CHARACTER_NAME_MAX) {
    return { ok: false, problem: "length" };
  }
  // Separators must sit between alphanumeric runs; no leading/trailing or doubles.
  if (!/^[A-Za-z0-9]+(?:[ '\-][A-Za-z0-9]+)*$/.test(display)) {
    return { ok: false, problem: "characters" };
  }
  return { ok: true, display, key: display.toLowerCase() };
}

/** Hard cap on the persisted unlock set — a ledger, not a bag. Raised from 512 for v49:
 *  the ledger now also carries the garage (`veh:<id>`) and a real gear catalog. */
export const CHARACTER_UNLOCKS_MAX = 1024;

/** A sane unlock token: short, ascii, `:`/`-`/`_` separated. Deliberately NOT checked
 *  against STYLE_VARIANTS — a wardrobe entry retired later must not invalidate the
 *  record of having earned it, and non-cosmetic unlock namespaces can join the set. */
const UNLOCK_TOKEN = /^[A-Za-z0-9][A-Za-z0-9:_-]{0,63}$/;

/**
 * Sanitize an untrusted unlock set: keep the well-formed tokens, in first-seen order,
 * deduped and capped. Returns null only when there is no array at all.
 *
 * TOLERANT, per the `validateInventory` precedent in rpg.ts (an unknown item id `continue`s
 * rather than failing the whole inventory, "or every save from before the weapons were
 * retired would validate to null and fall back to a fresh starter kit, wiping the player's
 * bags"). The same argument holds here and is why this does NOT follow the stricter
 * `stage`/`capitalLocalPosition` precedent in this file, which rejects the record: unlocks
 * are an earned cosmetic ledger appended to over a character's whole life, and one bad
 * token must never cost a player their character.
 */
export function sanitizeUnlocks(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out: string[] = [];
  const seen = new Set<string>();
  for (const rawEntry of raw) {
    if (typeof rawEntry !== "string" || !UNLOCK_TOKEN.test(rawEntry)) continue;
    const entry = canonicalWardrobeToken(rawEntry);
    if (seen.has(entry)) continue;
    seen.add(entry);
    out.push(entry);
    if (out.length >= CHARACTER_UNLOCKS_MAX) break;
  }
  return out;
}

function vec3(raw: unknown): [number, number, number] | null {
  return Array.isArray(raw) && raw.length === 3 && raw.every(Number.isFinite)
    ? [Number(raw[0]), Number(raw[1]), Number(raw[2])]
    : null;
}

/** Validate an untrusted record returned by HTTP/Firestore. `uid` (when known)
 *  pins the hometown to the owner's per-character home system; `cid` (when the
 *  caller knows which slot the record came from) additionally pins the record's
 *  own id to that slot. */
export function validateCharacterRecord(raw: unknown, uid?: string, cid?: string): CharacterRecordV1 | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== CHARACTER_RECORD_VERSION || (o.status !== "intro" && o.status !== "active")) return null;
  if (!isCharacterId(o.id)) return null;
  if (cid !== undefined && o.id !== cid) return null;
  const normalized = normalizeCharacterName(o.name);
  if (!normalized.ok || o.nameKey !== normalized.key) return null;
  const style = validateStyle(o.style);
  if (!style) return null;
  const h = o.hometown;
  if (!h || typeof h !== "object" || Array.isArray(h)) return null;
  const home = h as Record<string, unknown>;
  const localPosition = vec3(home.localPosition);
  const localFacing = vec3(home.localFacing);
  const capitalLocalPosition = home.capitalLocalPosition === undefined ? null : vec3(home.capitalLocalPosition);
  const systemId = typeof home.systemId === "string" ? home.systemId : "";
  if (uid !== undefined && systemId !== homeSystemId(uid, o.id)) return null;
  if (home.planetIndex !== 0 || !localPosition || !localFacing
    || (home.capitalLocalPosition !== undefined && !capitalLocalPosition)) return null;
  if (typeof home.settlementId !== "string" || typeof home.settlementName !== "string"
    || typeof home.capitalSettlementId !== "string" || typeof home.capitalName !== "string"
    || typeof home.distanceToCapitalM !== "number" || !Number.isFinite(home.distanceToCapitalM)) return null;
  const intro = o.intro;
  if (!intro || typeof intro !== "object" || Array.isArray(intro)) return null;
  const introRecord = intro as Record<string, unknown>;
  if (introRecord.id !== CHARACTER_INTRO_ID || typeof introRecord.completed !== "boolean") return null;
  if (o.stage !== undefined && !isStoryStage(o.stage)) return null;
  // Settled is the staged flow's "active"; the two must never disagree.
  // (2026-07-24: "prologue:ruin" renamed to "prologue:founding" — the capital
  // is NOT razed; a stale dev record with the old id parses as invalid.)
  if (o.stage === "settled" && o.status !== "active") return null;
  if (typeof o.createdAt !== "number" || !Number.isFinite(o.createdAt)
    || typeof o.updatedAt !== "number" || !Number.isFinite(o.updatedAt)) return null;
  // A malformed deletion stamp REJECTS the record rather than dropping to
  // "live": silently reviving a character the player deleted is the one failure
  // mode this field must never have.
  if (o.deletedAt !== undefined
    && (typeof o.deletedAt !== "number" || !Number.isFinite(o.deletedAt))) return null;
  // Absent, malformed, or entirely junk unlocks all validate to "no unlocks" and the
  // field is simply omitted — see sanitizeUnlocks for why this drops instead of rejecting.
  const unlocks = o.unlocks === undefined ? null : sanitizeUnlocks(o.unlocks);
  return {
    v: CHARACTER_RECORD_VERSION,
    id: o.id,
    status: o.status,
    name: normalized.display,
    nameKey: normalized.key,
    style,
    hometown: {
      systemId,
      planetIndex: 0,
      settlementId: home.settlementId,
      settlementName: home.settlementName,
      capitalSettlementId: home.capitalSettlementId,
      capitalName: home.capitalName,
      distanceToCapitalM: Math.max(0, home.distanceToCapitalM),
      localPosition,
      ...(capitalLocalPosition ? { capitalLocalPosition } : {}),
      localFacing,
    },
    intro: { id: CHARACTER_INTRO_ID, completed: introRecord.completed },
    ...(o.stage !== undefined ? { stage: o.stage as StoryStageId } : {}),
    ...(unlocks && unlocks.length > 0 ? { unlocks } : {}),
    ...(o.deletedAt !== undefined ? { deletedAt: o.deletedAt as number } : {}),
    createdAt: o.createdAt,
    updatedAt: o.updatedAt,
  };
}
